import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import api, { downloadFile } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, FileText, Download } from 'lucide-react';

const MONTHS = ['','January','February','March','April','May','June','July','August','September','October','November','December'];

export default function PayslipsPage() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewOpen, setViewOpen] = useState(false);
  const [payslipHtml, setPayslipHtml] = useState('');
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    api.get('/payslips/my')
      .then(res => setPayslips(res.data))
      .catch(() => toast.error('Failed to load payslips'))
      .finally(() => setLoading(false));
  }, []);

  const viewPayslip = async (payslipId) => {
    try {
      const res = await api.get(`/payslips/${payslipId}`);
      setPayslipHtml(res.data.html);
      setViewOpen(true);
    } catch {
      toast.error('Failed to load payslip');
    }
  };

  const handleDownload = async (ps) => {
    setDownloading(ps.id);
    try {
      await downloadFile(
        `/payslips/${ps.id}/download`,
        `payslip_${ps.employee_id}_${MONTHS[ps.month]}_${ps.year}.pdf`
      );
      toast.success('Payslip downloaded');
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading('');
    }
  };

  return (
    <div data-testid="payslips-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">My Payslips</h2>
        <p className="text-sm text-muted-foreground font-body mt-1">View and download your monthly payslip records</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : payslips.length === 0 ? (
        <div className="border border-border bg-card rounded-sm p-12 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">No payslips available yet</p>
        </div>
      ) : (
        <div className="border border-border bg-card rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-widest">Period</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Gross Earnings</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Deductions</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Net Pay</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Days</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.map(ps => (
                <TableRow key={ps.id} data-testid={`my-payslip-${ps.month}-${ps.year}`}>
                  <TableCell className="font-mono text-sm">{MONTHS[ps.month]} {ps.year}</TableCell>
                  <TableCell className="font-mono text-sm">₹{ps.total_earnings?.toLocaleString('en-IN')}</TableCell>
                  <TableCell className="font-mono text-sm text-red-600">-₹{ps.total_deductions?.toLocaleString('en-IN')}</TableCell>
                  <TableCell className="font-mono text-sm font-medium text-[#002FA7]">₹{ps.net_pay?.toLocaleString('en-IN')}</TableCell>
                  <TableCell className="font-mono text-xs">{ps.present_days}/{ps.working_days}</TableCell>
                  <TableCell>
                    <Badge className={ps.status === 'finalized' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                      {ps.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => viewPayslip(ps.id)} data-testid={`view-my-payslip-${ps.id}`}>
                        <Eye className="w-4 h-4 mr-1" strokeWidth={1.5} /> View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(ps)}
                        disabled={downloading === ps.id}
                        data-testid={`download-payslip-${ps.id}`}
                        className="text-[#002FA7] hover:text-[#002585]"
                      >
                        <Download className="w-4 h-4 mr-1" strokeWidth={1.5} />
                        {downloading === ps.id ? '...' : 'Download'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Payslip</DialogTitle>
            <DialogDescription>Your payslip document</DialogDescription>
          </DialogHeader>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(payslipHtml) }} className="border border-border rounded-sm" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
